import { SCROLL_SPEED, VIRTUAL_HEIGHT, VIRTUAL_WIDTH } from "../../config";
import { fillRoundedRect, strokeRoundedRect } from "../../utils/draw";
import { Background, paletteFor } from "../entities/Background";
import { Ground } from "../entities/Ground";
import { KiwiFlock } from "../entities/KiwiFlock";
import { Lizard } from "../entities/Lizard";
import type { Button } from "../systems/Buttons";
import { drawIconButton } from "../systems/Buttons";
import { BattlesScene } from "./BattlesScene";
import { FriendsScene } from "./FriendsScene";
import { LeaderboardScene } from "./LeaderboardScene";
import { globalChannel } from "../../../shared/chat.js";
import { ChatScene } from "./ChatScene";
import { ClanScene } from "./ClanScene";
import { DailyScene } from "./DailyScene";
import { ModScene } from "./ModScene";
import { PlayScene } from "./PlayScene";
import { QuestsScene } from "./QuestsScene";
import { SlotScene } from "./SlotScene";
import { RaceScene } from "./RaceScene";
import type { GameContext, Scene } from "./Scene";
import { ShopScene } from "./ShopScene";

// All rows share a 160px-wide column (center ± 80) with even 8px vertical gaps.
// The Shop/Battles/Race row is three 48px buttons (48·3 + 8·2 = 160) so its
// outer edges line up with the full-width rows above and below.
const LEADERBOARD_BTN = { x: VIRTUAL_WIDTH / 2 - 80, y: 360, w: 160, h: 40 };
const SHOP_BTN = { x: VIRTUAL_WIDTH / 2 - 80, y: 408, w: 48, h: 32 };
const BATTLES_BTN = { x: VIRTUAL_WIDTH / 2 - 24, y: 408, w: 48, h: 32 };
const RACE_BTN = { x: VIRTUAL_WIDTH / 2 + 32, y: 408, w: 48, h: 32 };
const FRIENDS_BTN = { x: VIRTUAL_WIDTH / 2 - 80, y: 448, w: 76, h: 32 };
const QUESTS_BTN = { x: VIRTUAL_WIDTH / 2 + 4, y: 448, w: 76, h: 32 };
const CLAN_BTN = { x: VIRTUAL_WIDTH / 2 - 80, y: 488, w: 76, h: 32 };
const CHAT_BTN = { x: VIRTUAL_WIDTH / 2 + 4, y: 488, w: 76, h: 32 };
const ACCOUNT_BTN = { x: VIRTUAL_WIDTH / 2 - 80, y: 524, w: 160, h: 32 };
const SIGNUP_BTN = { x: VIRTUAL_WIDTH / 2 - 80, y: 524, w: 76, h: 32 };
const LOGIN_BTN = { x: VIRTUAL_WIDTH / 2 + 4, y: 524, w: 76, h: 32 };
const MOD_BTN = { x: VIRTUAL_WIDTH / 2 - 80, y: 560, w: 160, h: 32 };

export class MenuScene implements Scene {
  private readonly bg = new Background();
  private readonly ground = new Ground();
  private readonly kiwis = new KiwiFlock();
  private readonly lizard = new Lizard(VIRTUAL_WIDTH * 0.4, VIRTUAL_HEIGHT * 0.5);
  private leaderboardHover = false;
  private accountHover = false;
  // Guards async callbacks below: a network refresh can resolve after the player
  // has already flapped into a new run, and re-running buildButtons() then would
  // stamp the menu's buttons onto the live PlayScene.
  private entered = false;

  constructor(private readonly ctxMgr: GameContext) {}

  enter(): void {
    this.entered = true;
    this.ctxMgr.audio.music.setMode("jazz");
    void this.ctxMgr.leaderboard.refresh();
    if (this.ctxMgr.settings.isLoggedIn()) {
      void this.ctxMgr.shop.refresh().then(() => {
        if (!this.entered) return;
        this.applyLizardSkin();
        this.buildButtons(); // admin access may have loaded — re-show the mod button
      });
      void this.ctxMgr.battles.refresh(
        this.ctxMgr.settings.playerName,
        this.ctxMgr.settings.authToken,
      );
      void this.ctxMgr.quests
        .refresh(this.ctxMgr.settings.playerName, this.ctxMgr.settings.authToken)
        .then(() => {
          if (this.entered) this.buildButtons(); // surface the claimable badge
        });
      void this.ctxMgr.daily
        .refresh(this.ctxMgr.settings.playerName, this.ctxMgr.settings.authToken)
        .then(() => {
          if (this.entered) this.buildButtons(); // surface the daily-reward dot
        });
    }
    this.applyLizardSkin();
    this.buildButtons();
  }

  private buildButtons(): void {
    const buttons: Button[] = [
      {
        ...LEADERBOARD_BTN,
        draw: (ctx, hit) => {
          this.leaderboardHover = hit;
          drawWideButton(ctx, LEADERBOARD_BTN, hit, "Leaderboard");
        },
        onPress: () => this.ctxMgr.goTo(new LeaderboardScene(this.ctxMgr, this.ctxMgr.renderer)),
      },
      ...this.shopButton(),
      ...this.battlesButton(),
      ...this.raceButton(),
      ...this.friendsButton(),
      ...this.questsButton(),
      ...this.clanButton(),
      ...this.globalChatButton(),
      ...this.dailyButton(),
      ...this.slotsButton(),
      ...this.accountButtons(),
      {
        x: 8,
        y: 8,
        w: 28,
        h: 28,
        draw: (ctx, hit) =>
          drawIconButton(
            ctx,
            { x: 8, y: 8, w: 28, h: 28, draw: () => undefined, onPress: () => undefined },
            hit,
            drawTikTokIcon,
          ),
        onPress: () =>
          window.open("https://www.tiktok.com/@Lizard.Flap", "_blank", "noopener,noreferrer"),
      },
      {
        x: VIRTUAL_WIDTH - 36,
        y: 8,
        w: 28,
        h: 28,
        draw: (ctx, hit) =>
          drawIconButton(
            ctx,
            { x: VIRTUAL_WIDTH - 36, y: 8, w: 28, h: 28, draw: () => undefined, onPress: () => undefined },
            hit,
            (c, x, y) => drawMuteIcon(c, x, y, this.ctxMgr.settings.muted),
          ),
        onPress: () => {
          const muted = this.ctxMgr.settings.toggleMuted();
          this.ctxMgr.audio.setMuted(muted);
        },
      },
    ];

    if (this.ctxMgr.mods.isAdmin(this.ctxMgr.settings.playerName) || this.ctxMgr.shop.isAdmin()) {
      buttons.push({
        ...MOD_BTN,
        draw: (ctx, hit) => drawWideButton(ctx, MOD_BTN, hit, "Mod menu", true),
        onPress: () => this.openModMenu(),
      });
    }

    this.ctxMgr.buttons.set(buttons);
  }

  exit(): void {
    this.entered = false;
    this.ctxMgr.buttons.clear();
  }

  onFlap(): void {
    this.ctxMgr.audio.resume();
    if (!this.ctxMgr.settings.hasName()) {
      void this.ctxMgr.promptForName().then((ok) => {
        if (ok) this.ctxMgr.goTo(new PlayScene(this.ctxMgr));
      });
      return;
    }
    this.ctxMgr.goTo(new PlayScene(this.ctxMgr));
  }

  private openModMenu(): void {
    if (this.ctxMgr.mods.ensureUnlocked()) {
      this.ctxMgr.goTo(new ModScene(this.ctxMgr));
    }
  }

  private applyLizardSkin(): void {
    this.lizard.setSkin(
      this.ctxMgr.shop.getEquippedCharacterRender(),
      this.ctxMgr.shop.getEquippedHatRender(),
    );
    this.lizard.setMods(this.ctxMgr.mods.flags);
  }

  private shopButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    return [
      {
        ...SHOP_BTN,
        draw: (ctx, hit) => drawWideButton(ctx, SHOP_BTN, hit, "Shop", true),
        onPress: () => this.ctxMgr.goTo(new ShopScene(this.ctxMgr)),
      },
    ];
  }

  private friendsButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    return [
      {
        ...FRIENDS_BTN,
        draw: (ctx, hit) => {
          drawWideButton(ctx, FRIENDS_BTN, hit, "Friends", true);
          const n = this.ctxMgr.friends.pendingCount() + this.ctxMgr.friends.dmUnread();
          if (n > 0) drawBadge(ctx, FRIENDS_BTN.x + FRIENDS_BTN.w - 4, FRIENDS_BTN.y + 2, n);
        },
        onPress: () => this.ctxMgr.goTo(new FriendsScene(this.ctxMgr)),
      },
    ];
  }

  private questsButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    return [
      {
        ...QUESTS_BTN,
        draw: (ctx, hit) => {
          drawWideButton(ctx, QUESTS_BTN, hit, "Quests", true);
          const n = this.ctxMgr.quests.claimableCount();
          if (n > 0) drawBadge(ctx, QUESTS_BTN.x + QUESTS_BTN.w - 4, QUESTS_BTN.y + 2, n);
        },
        onPress: () => this.ctxMgr.goTo(new QuestsScene(this.ctxMgr)),
      },
    ];
  }

  private dailyButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    const RECT = { x: 44, y: 8, w: 28, h: 28 };
    return [
      {
        ...RECT,
        draw: (ctx, hit) => {
          drawIconButton(
            ctx,
            { ...RECT, draw: () => undefined, onPress: () => undefined },
            hit,
            drawGiftIcon,
          );
          if (this.ctxMgr.daily.claimable()) drawDot(ctx, RECT.x + RECT.w - 4, RECT.y + 2);
        },
        onPress: () => this.ctxMgr.goTo(new DailyScene(this.ctxMgr)),
      },
    ];
  }

  private slotsButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    const RECT = { x: 80, y: 8, w: 28, h: 28 };
    return [
      {
        ...RECT,
        draw: (ctx, hit) =>
          drawIconButton(
            ctx,
            { ...RECT, draw: () => undefined, onPress: () => undefined },
            hit,
            drawSlotIcon,
          ),
        onPress: () => this.ctxMgr.goTo(new SlotScene(this.ctxMgr)),
      },
    ];
  }

  private clanButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    return [
      {
        ...CLAN_BTN,
        draw: (ctx, hit) => {
          drawWideButton(ctx, CLAN_BTN, hit, "Clan", true);
          const n = this.ctxMgr.friends.clanUnread() + this.ctxMgr.friends.clanRequestCount();
          if (n > 0) drawBadge(ctx, CLAN_BTN.x + CLAN_BTN.w - 4, CLAN_BTN.y + 2, n);
        },
        onPress: () => this.ctxMgr.goTo(new ClanScene(this.ctxMgr)),
      },
    ];
  }

  private globalChatButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    return [
      {
        ...CHAT_BTN,
        draw: (ctx, hit) => {
          drawWideButton(ctx, CHAT_BTN, hit, "Chat", true);
          const n = this.ctxMgr.friends.globalUnread();
          if (n > 0) drawBadge(ctx, CHAT_BTN.x + CHAT_BTN.w - 4, CHAT_BTN.y + 2, n);
        },
        onPress: () =>
          this.ctxMgr.goTo(
            new ChatScene(this.ctxMgr, globalChannel(), "GLOBAL CHAT", () =>
              this.ctxMgr.goTo(new MenuScene(this.ctxMgr)),
            ),
          ),
      },
    ];
  }

  private battlesButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    return [
      {
        ...BATTLES_BTN,
        draw: (ctx, hit) => {
          drawWideButton(ctx, BATTLES_BTN, hit, "Battles", true);
          const n = this.ctxMgr.battles.actionableCount(this.ctxMgr.settings.playerName);
          if (n > 0) drawBadge(ctx, BATTLES_BTN.x + BATTLES_BTN.w - 4, BATTLES_BTN.y + 2, n);
        },
        onPress: () => this.ctxMgr.goTo(new BattlesScene(this.ctxMgr)),
      },
    ];
  }

  private raceButton(): Button[] {
    if (!this.ctxMgr.settings.isLoggedIn()) return [];
    return [
      {
        ...RACE_BTN,
        draw: (ctx, hit) => drawWideButton(ctx, RACE_BTN, hit, "Race", true),
        onPress: () => this.ctxMgr.goTo(new RaceScene(this.ctxMgr)),
      },
    ];
  }

  private accountButtons(): Button[] {
    const settings = this.ctxMgr.settings;
    if (settings.isLoggedIn()) {
      return [
        {
          ...ACCOUNT_BTN,
          draw: (ctx, hit) => {
            this.accountHover = hit;
            drawWideButton(ctx, ACCOUNT_BTN, hit, `Logout (${settings.playerName})`, true);
          },
          onPress: () => {
            if (window.confirm(`Log out of "${settings.playerName}"?`)) {
              settings.logout();
              this.ctxMgr.shop.clear();
              this.ctxMgr.friends.clear();
              this.ctxMgr.quests.clear();
              this.ctxMgr.chat.clear();
              this.ctxMgr.clans.clear();
              this.ctxMgr.daily.clear();
              this.enter();
            }
          },
        },
      ];
    }
    return [
      {
        ...SIGNUP_BTN,
        draw: (ctx, hit) => {
          this.accountHover = hit;
          drawWideButton(ctx, SIGNUP_BTN, hit, "Sign up", true);
        },
        onPress: () => {
          void this.ctxMgr.promptForName().then((ok) => {
            if (ok) this.enter();
          });
        },
      },
      {
        ...LOGIN_BTN,
        draw: (ctx, hit) => drawWideButton(ctx, LOGIN_BTN, hit, "Log in", true),
        onPress: () => {
          void this.ctxMgr.promptForLogin().then((ok) => {
            if (ok) this.enter();
          });
        },
      },
    ];
  }

  update(dt: number): void {
    this.bg.update(dt, SCROLL_SPEED);
    this.ground.update(dt, SCROLL_SPEED);
    this.kiwis.update(dt, this.bg);
    this.lizard.setMods(this.ctxMgr.mods.flags);
    this.lizard.update(dt);
  }

  render(ctx: CanvasRenderingContext2D, alpha: number): void {
    const palette = paletteFor(this.ctxMgr.shop.getEquippedBackgroundRender(false), null, false);
    this.bg.render(ctx, palette);
    this.kiwis.render(ctx, this.bg, palette.isNight);
    this.ground.render(ctx);
    this.lizard.render(ctx, alpha);

    ctx.fillStyle = "#1a1f2b";
    ctx.font = "bold 32px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("LIZARD FLAP", VIRTUAL_WIDTH / 2, 110);

    ctx.font = "16px system-ui, sans-serif";
    ctx.fillText("tap / space to flap", VIRTUAL_WIDTH / 2, 150);

    ctx.font = "bold 13px system-ui, sans-serif";
    ctx.fillText("Hit pipe & fly milestones to unlock collections!", VIRTUAL_WIDTH / 2, 178);

    if (this.ctxMgr.score.high > 0) {
      // Moved above the playfield — the lower menu now stacks down to ~y=592.
      ctx.fillText(`best: ${this.ctxMgr.score.high}`, VIRTUAL_WIDTH / 2, 204);
    }

    ctx.fillStyle = "#1a1f2b";
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillText("By: Daniel Guzev", VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT - 14);

    void this.leaderboardHover;
    void this.accountHover;
  }
}

function drawWideButton(
  ctx: CanvasRenderingContext2D,
  rect: { x: number; y: number; w: number; h: number },
  hit: boolean,
  label: string,
  subtle = false,
): void {
  ctx.fillStyle = hit ? "#7a5e2a" : "#f5e9c8";
  fillRoundedRect(ctx, rect.x, rect.y, rect.w, rect.h, 8);
  ctx.strokeStyle = "#7a5e2a";
  ctx.lineWidth = 2;
  strokeRoundedRect(ctx, rect.x + 0.5, rect.y + 0.5, rect.w - 1, rect.h - 1, 8);
  ctx.fillStyle = hit ? "#f5e9c8" : "#7a5e2a";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = subtle ? "13px system-ui, sans-serif" : "bold 16px system-ui, sans-serif";
  ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2);
  ctx.textBaseline = "alphabetic";
}

function drawBadge(ctx: CanvasRenderingContext2D, cx: number, cy: number, n: number): void {
  ctx.fillStyle = "#e53935";
  ctx.beginPath();
  ctx.arc(cx, cy, 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.font = "bold 10px system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(String(n), cx, cy + 0.5);
  ctx.textBaseline = "alphabetic";
}

// Official TikTok glyph (Font Awesome single-path, viewBox 0 0 448 512).
const TIKTOK_PATH_D =
  "M448,209.9a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z";
// Built lazily: Path2D doesn't exist in the Node test env, so constructing it at
// module load would crash any test that imports this module.
let tiktokPath: Path2D | null = null;

export function drawTikTokIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  tiktokPath ??= new Path2D(TIKTOK_PATH_D);
  const scale = 18 / 512; // fit the 28px button with a little padding
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);
  ctx.translate(-224, -256); // center the 448×512 glyph
  ctx.fillStyle = "#fff";
  ctx.fill(tiktokPath);
  ctx.restore();
}

// A little wrapped present (daily-reward icon).
function drawGiftIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.fillStyle = "#fff";
  ctx.strokeStyle = "#fff";
  ctx.lineWidth = 1.5;
  // box
  ctx.fillRect(cx - 7, cy - 2, 14, 9);
  // lid
  ctx.fillRect(cx - 8, cy - 5, 16, 4);
  // ribbon
  ctx.strokeStyle = "#7a5e2a";
  ctx.beginPath();
  ctx.moveTo(cx, cy - 5);
  ctx.lineTo(cx, cy + 7);
  ctx.stroke();
  // bow
  ctx.beginPath();
  ctx.moveTo(cx, cy - 5);
  ctx.lineTo(cx - 4, cy - 8);
  ctx.moveTo(cx, cy - 5);
  ctx.lineTo(cx + 4, cy - 8);
  ctx.stroke();
}

// A tiny slot-machine / gamble icon (three reels under a top bar).
function drawSlotIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.strokeStyle = "#fff";
  ctx.fillStyle = "#fff";
  ctx.lineWidth = 1.5;
  // body
  ctx.strokeRect(cx - 8, cy - 6, 16, 13);
  // top bar
  ctx.fillRect(cx - 8, cy - 9, 16, 3);
  // three reel slits
  for (let i = 0; i < 3; i += 1) ctx.fillRect(cx - 6 + i * 5, cy - 3, 3, 7);
}

// A small notification dot (no number).
function drawDot(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.fillStyle = "#e53935";
  ctx.beginPath();
  ctx.arc(cx, cy, 5, 0, Math.PI * 2);
  ctx.fill();
}

export function drawMuteIcon(ctx: CanvasRenderingContext2D, cx: number, cy: number, muted: boolean): void {
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.moveTo(cx - 6, cy - 4);
  ctx.lineTo(cx - 1, cy - 4);
  ctx.lineTo(cx + 4, cy - 8);
  ctx.lineTo(cx + 4, cy + 8);
  ctx.lineTo(cx - 1, cy + 4);
  ctx.lineTo(cx - 6, cy + 4);
  ctx.closePath();
  ctx.fill();
  if (muted) {
    ctx.strokeStyle = "#e53935";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx - 7, cy - 7);
    ctx.lineTo(cx + 7, cy + 7);
    ctx.stroke();
  } else {
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cx + 7, cy, 3, -0.6, 0.6);
    ctx.stroke();
  }
}
